// src/components/AugustPractices.js
// August Practices — "Your Magic Toolkit." A permanent library of reusable gratitude tools
// that unlock as Journey progress reaches each one's day, replacing the standalone Tracklist
// tab in the bottom nav (Tracklist itself is untouched — it now lives inside Journey).
import React, { useEffect, useRef, useState } from 'react'
import { PRACTICES } from '../data/content'
import AugustTexture from './AugustTexture'
import PageHeader from './ui/PageHeader'
import './AugustPractices.css'

const WALLPAPER_LINES = [
  "Some days are for ten gratitudes. Some days ask for one more thing.",
  "A tool you don't need today is still worth knowing where it lives.",
  "Return to what already worked before",
  "The rock stays in your pocket long after day two",
  "Nothing here is homework. Everything here is optional.",
  "You unlocked this on your own time",
  "Come back to it whenever you need it",
  "One small thank you at a time",
]

const RINGS = [
  { width: 78, height: 78, top: -18, right: 20, opacity: 0.6 },
  { width: 56, height: 56, bottom: 100, left: -14, opacity: 0.42 },
]

const STATIC_SPLATS = [
  { width: 12, height: 10, top: 84, left: 24, opacity: 0.35, borderRadius: '58% 42% 55% 45%' },
  { width: 5,  height: 5,  top: 94, left: 40, opacity: 0.26, borderRadius: '50%' },
]

export default function AugustPractices({ completedCount = 0, onOpenPractice, augustTheme, onAugustThemeChange }) {
  const reduced = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ).current
  const [rowsIn, setRowsIn] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const t = setTimeout(() => setRowsIn(true), 120)
    return () => clearTimeout(t)
  }, [reduced])

  const wallpaper = Array.from({ length: 20 }, (_, i) => WALLPAPER_LINES[i % WALLPAPER_LINES.length])

  // Unlock is Journey progress, not points — permanent once reached, same n <= completedCount
  // gating AugustTracklist.js already uses for tracks. status: 'future' (Magic Check) is never
  // reachable regardless of progress — it has no screen behind it yet.
  const rows = PRACTICES.map(practice => ({
    practice,
    unlocked: practice.status === 'active' && completedCount >= practice.unlockDay,
  }))

  return (
    <div className="aug-practices">
      <AugustTexture variant="corner" wallpaperLines={wallpaper} rings={RINGS} splats={STATIC_SPLATS} lineCount={20} />

      <div className="aug-practices-screen">
        <PageHeader
          eyebrow="Your Magic Toolkit"
          title="Practices"
          descriptor="Tools to return to, anytime"
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />

        <div className="aug-practices-list">
          {rows.map(({ practice, unlocked }, i) => (
            <div
              key={practice.id}
              className={`aug-practices-row${unlocked ? ' unlocked' : ' locked'}${rowsIn ? ' in' : ''}`}
              style={{ transitionDelay: reduced ? '0ms' : `${i * 40}ms` }}
              onClick={unlocked ? () => onOpenPractice(practice.id) : undefined}
            >
              <div className="aug-practices-row-top">
                <span className="aug-practices-mark">{unlocked ? '✦' : ''}</span>
                <span className="aug-practices-name">{practice.name}</span>
                {unlocked && <span className="aug-practices-chevron">→</span>}
              </div>
              <div className="aug-practices-tagline">
                {unlocked ? practice.tagline : `Unlocks Day ${practice.unlockDay}`}
              </div>
            </div>
          ))}
        </div>

        <div className="aug-practices-foot">Ten gratitudes are the ritual. This is everything else.</div>
      </div>
    </div>
  )
}
